/**
 * NPEAllocationListPage — committed NPE Allocations. Đồng bộ ContractListView.
 */
import { type MouseEvent, useMemo, useState } from 'react'
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, Popover, Stack, Table, TableBody, TableCell, TableHead, TablePagination, TableRow,
  TextField, Typography,
} from '@mui/material'
import { alpha, type SxProps, type Theme } from '@mui/material/styles'
import { TableVirtuoso } from 'react-virtuoso'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { useNavigate } from 'react-router-dom'

import ConfirmDialog from '@/components/ConfirmDialog'
import RoleGuard from '@/components/RoleGuard'
import { useSnackbar } from '@/hooks/useSnackbar'
import { usePagePermission } from '../../hooks/usePagePermission'
import {
  useDeleteNPEAllocationMutation, useListNPEAllocationsQuery, useCreateAllocationSessionMutation,
} from '@/store/api/npeAllocationsApi'
import { useListNPEFixationsQuery } from '@/store/api/npeFixationsApi'
import type { NPEAllocationOut, NPEFixationOut } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

export interface NPEAllocationListPageProps {
  embeddedNpeContract?: string
}

type FilterKind = 'search' | 'fixationNo'
const ALL_ROWS_PAGE_SIZE = 1_000_000

const filterBarBtnSx: SxProps<Theme> = (theme) => ({
  minWidth: 0, px: 0.75, py: 0.2, minHeight: 26, textTransform: 'none', fontSize: '0.7rem', lineHeight: 1.1,
  '&.MuiButton-outlined': { borderColor: alpha(theme.palette.text.primary, 0.14), '&:hover': { borderColor: alpha(theme.palette.text.primary, 0.24), backgroundColor: alpha(theme.palette.text.primary, 0.04) } },
})
const actionToolbarBtnSx: SxProps<Theme> = { minWidth: 0, px: 0.75, py: 0.2, minHeight: 26, fontSize: '0.72rem', lineHeight: 1.1, textTransform: 'none' }

type ColumnKind = 'text' | 'date' | 'number'
type ColumnDef = { key: keyof NPEAllocationOut; label: string; kind?: ColumnKind; align?: 'right'; digits?: number }

const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'allocation_id', label: 'ID', kind: 'number', align: 'right' },
  { key: 'allocation_date', label: 'Ngày Alloc', kind: 'date' },
  { key: 'fixation_no', label: 'Fixation No' },
  { key: 'advance_no', label: 'Advance No' },
  { key: 'npe_contract', label: 'NPE Contract' },
  { key: 'p_contract', label: 'P Contract' },
  { key: 'allocation_quantity', label: 'SL Alloc (kg)', kind: 'number', align: 'right', digits: 0 },
  { key: 'allocation_money', label: 'Tiền Alloc (VND)', kind: 'number', align: 'right', digits: 0 },
  { key: 'fixation_price', label: 'Giá fix', kind: 'number', align: 'right', digits: 2 },
  { key: 'allocation_interest', label: 'Interest', kind: 'number', align: 'right', digits: 0 },
  { key: 'note', label: 'Ghi chú' },
]

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const formatAccessDate = (v: unknown): string => { if (!v) return ''; const d = new Date(String(v)); if (Number.isNaN(d.getTime())) return String(v); return `${`${d.getDate()}`.padStart(2, '0')}-${ACCESS_MONTHS[d.getMonth()]}-${`${d.getFullYear()}`}` }
const formatNumberFixed = (v: number, digits = 0): string => digits > 0 ? v.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits }) : (Number.isInteger(v) ? v.toLocaleString('en-US') : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))

const rowMatchesSearch = (row: NPEAllocationOut, term: string): boolean => {
  const needle = term.trim().toLowerCase()
  if (!needle) return true
  const fields: Array<keyof NPEAllocationOut> = ['fixation_no', 'advance_no', 'npe_contract', 'p_contract', 'note']
  return fields.some((k) => { const v = row[k]; return v != null && String(v).toLowerCase().includes(needle) })
}

const cellValue = (row: NPEAllocationOut, col: ColumnDef): string => {
  const val = row[col.key]
  if (val == null) return ''
  if (col.kind === 'date') return formatAccessDate(val)
  if (col.kind === 'number' && typeof val === 'number') return formatNumberFixed(val, col.digits ?? 0)
  if (typeof val === 'number') return formatNumberFixed(val, 0)
  return String(val)
}

export function NPEAllocationListPage({ embeddedNpeContract }: NPEAllocationListPageProps = {}) {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()
  const navigate = useNavigate()

  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(-1)

  const [searchTerm, setSearchTerm] = useState('')
  const [fixationFilter, setFixationFilter] = useState('')

  const [filterPopover, setFilterPopover] = useState<{ kind: FilterKind; anchor: HTMLElement } | null>(null)
  const closeFilterPopover = () => setFilterPopover(null)
  const openFilterPopover = (kind: FilterKind, e: MouseEvent<HTMLElement>) => setFilterPopover({ kind, anchor: e.currentTarget })

  const hasActiveFilters = Boolean(searchTerm.trim() || fixationFilter)
  const clearAllFilters = () => { setSearchTerm(''); setFixationFilter(''); setPage(0) }

  const [confirmDel, setConfirmDel] = useState<NPEAllocationOut | null>(null)
  const [newOpen, setNewOpen] = useState(false)

  const { data, isLoading, isFetching, error } = useListNPEAllocationsQuery({ page: 1, page_size: ALL_ROWS_PAGE_SIZE })
  const [deleteAllocation, { isLoading: deleting }] = useDeleteNPEAllocationMutation()

  const baseRows = useMemo(() => {
    const items = data?.items ?? []
    return embeddedNpeContract ? items.filter((it) => it.npe_contract === embeddedNpeContract) : items
  }, [data, embeddedNpeContract])

  const filteredRows = useMemo(() =>
    baseRows.filter((row) => {
      if (!rowMatchesSearch(row, searchTerm)) return false
      if (fixationFilter && row.fixation_no !== fixationFilter) return false
      return true
    }), [baseRows, searchTerm, fixationFilter])

  const rows = useMemo(() => rowsPerPage === -1 ? filteredRows : filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage), [filteredRows, page, rowsPerPage])

  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}`
  const selectedRow = useMemo(() => baseRows.find((r) => r.allocation_id === selectedId) ?? null, [baseRows, selectedId])

  const handleViewLocalP = () => { if (!selectedRow?.p_contract) return; navigate(`/purchase/local-p/${encodeURIComponent(selectedRow.p_contract)}`) }
  const handleConfirmDel = async () => {
    if (!confirmDel) return
    try { await deleteAllocation(confirmDel.allocation_id).unwrap(); snack.success(`Đã xoá allocation #${confirmDel.allocation_id}`); setConfirmDel(null) }
    catch (err) { snack.error(getErrorMessage(err, 'Xoá allocation thất bại')) }
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" flexWrap="wrap" useFlexGap justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
        <Stack direction="row" spacing={0.375} flexWrap="wrap" useFlexGap alignItems="center" sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25 }}>Filter</Typography>
          <Button size="small" variant={searchTerm.trim() ? 'contained' : 'outlined'} color={searchTerm.trim() ? 'primary' : 'inherit'} endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />} onClick={(e) => openFilterPopover('search', e)} sx={filterBarBtnSx}>Search</Button>
          <Button size="small" variant={fixationFilter ? 'contained' : 'outlined'} color={fixationFilter ? 'primary' : 'inherit'} endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />} onClick={(e) => openFilterPopover('fixationNo', e)} sx={filterBarBtnSx}>Fixation No</Button>
          {hasActiveFilters && <Button size="small" variant="text" color="secondary" onClick={clearAllFilters} sx={filterBarBtnSx}>Clear All</Button>}
        </Stack>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
          <Button variant="contained" size="small" color="success" sx={actionToolbarBtnSx} disabled={!canEdit} onClick={() => setNewOpen(true)}>New</Button>
          <Button variant="outlined" size="small" color="primary" sx={actionToolbarBtnSx} disabled={!selectedRow?.p_contract} onClick={handleViewLocalP}>View P-HĐ</Button>
          <Button variant="outlined" size="small" color="error" sx={actionToolbarBtnSx} disabled={!selectedRow || !canEdit} onClick={() => selectedRow && setConfirmDel(selectedRow)}>Delete</Button>
        </Stack>
      </Stack>
      <Divider sx={{ mb: 0.5 }} />

      {error ? <Alert severity="error" sx={{ mb: 1 }}>Failed to load allocations.</Alert> : null}

      <Box className="app-dataframe">
        <Box className="app-dataframe-toolbar" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap', minHeight: 30, py: 0.125 }}>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontSize: '0.68rem' }}>Total: {filteredRows.length}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontSize: '0.68rem' }}>Loaded: {baseRows.length}</Typography>
            <TablePagination component="div" count={filteredRows.length} page={rowsPerPage === -1 ? 0 : page} onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0) }} rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]} labelRowsPerPage="Rows" sx={{ flexShrink: 0, '& .MuiTablePagination-toolbar': { minHeight: 32, px: 0.5 }, '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select': { fontSize: '0.72rem' } }} />
          </Stack>
        </Box>

        <Popover open={filterPopover !== null} anchorEl={filterPopover?.anchor ?? null} onClose={closeFilterPopover} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }} PaperProps={{ sx: { p: 1.5, minWidth: 240, maxWidth: 360 } }}>
          {filterPopover?.kind === 'search' && (
            <Stack spacing={1}>
              <TextField size="small" fullWidth autoFocus label="Tìm kiếm" placeholder="Fixation no, advance, NPE..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setPage(0) }} />
              <Button size="small" variant="text" onClick={() => { setSearchTerm(''); setPage(0); closeFilterPopover() }}>Clear Search</Button>
            </Stack>
          )}
          {filterPopover?.kind === 'fixationNo' && (
            <Stack spacing={1}>
              <TextField size="small" fullWidth autoFocus label="Fixation No" placeholder="NPE-XXXXX/PL01" value={fixationFilter} onChange={(e) => { setFixationFilter(e.target.value); setPage(0) }} />
              <Button size="small" variant="text" onClick={() => { setFixationFilter(''); setPage(0); closeFilterPopover() }}>Clear</Button>
            </Stack>
          )}
        </Popover>

        <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden' }}>
          <TableVirtuoso
            key={gridKey}
            style={{ height: '100%' }}
            data={rows}
            fixedHeaderContent={() => (
              <TableRow>
                {TABLE_COLUMNS.map((col) => (
                  <TableCell key={String(col.key)} align={col.align} sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: 'background.default' }}>{col.label}</TableCell>
                ))}
              </TableRow>
            )}
            itemContent={(_: number, row: NPEAllocationOut) =>
              TABLE_COLUMNS.map((col) => {
                const val = cellValue(row, col)
                return (
                  <TableCell key={String(col.key)} align={col.align} sx={{ whiteSpace: 'nowrap', py: 0.5 }}>
                    <Box component="span" title={val || '-'} sx={{ display: 'inline-block', width: '100%' }}>{val || '-'}</Box>
                  </TableCell>
                )
              })
            }
            components={{
              Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: 1900, width: '100%' }} />,
              TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
              TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
              TableRow: (props: any) => {
                const idx = Number((props as any)['data-item-index'])
                const id = Number.isFinite(idx) ? rows[idx]?.allocation_id : null
                return <TableRow {...props} hover selected={id != null && id === selectedId} sx={{ cursor: 'pointer' }} onClick={() => { if (id != null) setSelectedId(id) }} />
              },
            }}
          />
        </Box>
      </Box>

      <NewAllocationDialog open={newOpen} onClose={() => setNewOpen(false)} onCreated={(fixationId, sessionUid) => { setNewOpen(false); navigate(`/purchase/npe-fixations/${fixationId}/allocate?session=${encodeURIComponent(sessionUid)}`) }} />
      <ConfirmDialog open={!!confirmDel} title="Xoá Allocation" message={confirmDel ? `Bạn chắc chắn muốn xoá allocation #${confirmDel.allocation_id}?` : ''} destructive loading={deleting} onConfirm={() => void handleConfirmDel()} onCancel={() => setConfirmDel(null)} />
    </Box>
  )
}

function NewAllocationDialog({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (fixationId: number, sessionUid: string) => void }) {
  const snack = useSnackbar()
  const [selected, setSelected] = useState<NPEFixationOut | null>(null)

  const { data: fixations } = useListNPEFixationsQuery({ page: 1, page_size: 200 })
  const [createSession, { isLoading }] = useCreateAllocationSessionMutation()

  const handleCreate = async () => {
    if (!selected) return
    try {
      const res = await createSession({ fixation_id: selected.cs_fixation_id }).unwrap()
      const uid = res.data?.session_uid
      if (!uid) throw new Error('Missing session_uid')
      onCreated(selected.cs_fixation_id, uid)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Tạo session thất bại'))
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Mở Allocation Wizard</DialogTitle>
      <DialogContent dividers>
        <Box pt={1}>
          <Autocomplete
            size="small"
            options={fixations?.items ?? []}
            value={selected}
            onChange={(_e, v) => setSelected(v)}
            getOptionLabel={(opt) => opt ? `${opt.fixation_no} — ${opt.supplier_name ?? ''}` : ''}
            isOptionEqualToValue={(o, v) => o.cs_fixation_id === v.cs_fixation_id}
            renderInput={(params) => <TextField {...params} label="Chọn Fixation *" />}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>Huỷ</Button>
        <Button variant="contained" color="primary" disabled={!selected || isLoading} onClick={() => void handleCreate()}>Tạo session &amp; mở wizard</Button>
      </DialogActions>
    </Dialog>
  )
}

export default NPEAllocationListPage
